import type { BlockRule, Category, OptionalCategory } from '@cmp/shared';
import { isProtected } from '@cmp/shared';

/**
 * Script and iframe gating.
 *
 * Two modes work together:
 *  1. Manual: authors mark tags `type="text/plain" data-cmp="analytics"`. Deterministic, recommended.
 *  2. Auto: a MutationObserver plus a patched document.createElement neutralise scripts and
 *     iframes whose URL matches a rule before they execute (the pattern popularised by yett).
 *     Requires this script to be the first script in <head>.
 *     Limitation shared with every DOM-level blocker: for tags the HTML parser inserts, the browser
 *     may start the network fetch before we can intervene. Execution is prevented, but an iframe
 *     document can still be requested. Authors should mark parser-inserted embeds with
 *     `data-cmp="marketing" data-cmp-src="https://..."` (no `src`) for a guaranteed no-request.
 *
 * Blocked nodes are kept so they can be re-injected once the matching category is granted.
 */

export const BLOCKED_TYPE = 'javascript/blocked';

/**
 * Inline code that calls a held library straight away (a HubSpot form's `hbspt.forms.create(...)`
 * right after its loader). Run early it throws and the form never appears; so it is held with its
 * library and run once the library has loaded.
 */
const DEPENDENTS: { global: RegExp; source: string }[] = [
  { global: /\bhbspt\./, source: 'hsforms' },
  { global: /\bhbspt\./, source: 'hs-scripts' },
];
const MANUAL_TYPE = 'text/plain';
const ATTR = 'data-cmp';

interface BlockedScript {
  kind: 'script';
  category: OptionalCategory;
  node: HTMLScriptElement;
  /** Inert stand-in left where a parser-inserted script was, so the page keeps its shape and order. */
  stand?: HTMLScriptElement;
  /** Inline code that needs this held script's library: run only after that library has loaded. */
  after?: HTMLScriptElement;
  /** True when the author gated it explicitly (text/plain + data-cmp). */
  manual: boolean;
}
interface BlockedFrame {
  kind: 'iframe';
  category: OptionalCategory;
  node: HTMLIFrameElement;
  src: string;
  manual: boolean;
}
type Blocked = BlockedScript | BlockedFrame;

export type PlaceholderRenderer = (frame: HTMLIFrameElement, category: OptionalCategory) => void;

export class Blocker {
  /** Only one blocker may be live per document; a second start() supersedes the first. */
  private static active: Blocker | null = null;
  private rules: BlockRule[] = [];
  private blocked: Blocked[] = [];
  private granted = new Set<Category>(['necessary']);
  private observer: MutationObserver | null = null;
  private started = false;
  private placeholder: PlaceholderRenderer | null = null;
  /** Scripts the page created itself (via createElement). Held in place and released as the same node. */
  private dynamic = new WeakSet<HTMLScriptElement>();
  /** Released copies of held scripts, and whether each has loaded, so dependent inline code can wait. */
  private released = new WeakMap<HTMLScriptElement, HTMLScriptElement>();
  private loaded = new WeakSet<HTMLScriptElement>();
  private srcSet: ((el: HTMLScriptElement, v: string) => void) | null = null;
  private typeSet: ((el: HTMLScriptElement, v: string) => void) | null = null;

  constructor(rules: BlockRule[], placeholder?: PlaceholderRenderer) {
    this.rules = rules.map((r) => ({ match: r.match.toLowerCase(), category: r.category }));
    this.placeholder = placeholder ?? null;
  }

  addRules(rules: BlockRule[]): void {
    for (const r of rules) this.rules.push({ match: r.match.toLowerCase(), category: r.category });
  }

  setPlaceholder(renderer: PlaceholderRenderer): void {
    this.placeholder = renderer;
    for (const b of this.blocked) if (b.kind === 'iframe') renderer(b.node, b.category);
  }

  /** Manual mode: drop URL rules and let anything auto-detected so far run. Manual tags stay gated. */
  disableAutoBlock(): void {
    this.rules = [];
    const keep: Blocked[] = [];
    for (const b of this.blocked) {
      if (b.manual) keep.push(b);
      else this.activate(b);
    }
    this.blocked = keep;
  }

  /** Category a URL belongs to, or null if it is not gated. */
  categoryFor(value: unknown): OptionalCategory | null {
    // src may be a TrustedScriptURL object, not a string; never call string methods on it directly.
    const url = value == null ? '' : String(value);
    if (!url) return null;
    // Payments, checkout, fraud checks, captchas and sign-in always run. No rule can hold them.
    if (isProtected(url)) return null;
    const u = url.toLowerCase();
    for (const r of this.rules) if (u.indexOf(r.match) !== -1) return r.category;
    return null;
  }

  /** True if a message came from one of the frames we are holding (its placeholder's "Load content" button). */
  isHeldFrame(win: MessageEventSource | null): boolean {
    return !!win && this.blocked.some((b) => b.kind === 'iframe' && b.node.contentWindow === win);
  }

  isGranted(category: Category): boolean {
    return this.granted.has(category);
  }

  /** Begin intercepting. Safe to call once; later calls are ignored. */
  start(): void {
    if (this.started) return;
    this.started = true;
    Blocker.active?.stop();
    Blocker.active = this;
    this.patchCreateElement();
    this.observer = new MutationObserver((muts) => {
      for (const m of muts) {
        for (let i = 0; i < m.addedNodes.length; i++) {
          const n = m.addedNodes[i]!;
          this.inspect(n);
          // Whole subtrees arrive at once (innerHTML, template cloning, page swaps): look inside too.
          if (n.nodeType === 1 && (n as Element).firstElementChild) (n as Element).querySelectorAll('script,iframe').forEach((c) => this.inspect(c));
        }
      }
    });
    this.observer.observe(document.documentElement, { childList: true, subtree: true });
    // Anything already in the DOM (e.g. manual text/plain tags in <head> before us).
    this.sweep();
  }

  /** Stop observing. The createElement patch stays installed but becomes a no-op for this instance. */
  stop(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (Blocker.active === this) Blocker.active = null;
  }

  private isActive(): boolean {
    return Blocker.active === this;
  }

  /** Release everything whose category is now allowed. Idempotent. */
  release(granted: Category[]): void {
    this.granted = new Set<Category>(['necessary', ...granted]);
    const keep: Blocked[] = [];
    for (const b of this.blocked) {
      if (this.granted.has(b.category)) this.activate(b);
      else keep.push(b);
    }
    this.blocked = keep;
  }

  /** Re-scan the document for manual tags, useful for SPAs after route changes. */
  sweep(): void {
    const scripts = document.querySelectorAll<HTMLScriptElement>(`script[type="${MANUAL_TYPE}"][${ATTR}]`);
    scripts.forEach((s) => this.inspect(s));
    const frames = document.querySelectorAll<HTMLIFrameElement>(`iframe[${ATTR}-src]`);
    frames.forEach((f) => this.inspect(f));
  }

  private inspect(node: Node): void {
    if (node.nodeType !== 1) return;
    const el = node as HTMLElement;
    if (el.tagName === 'SCRIPT') this.inspectScript(el as HTMLScriptElement);
    else if (el.tagName === 'IFRAME') this.inspectFrame(el as HTMLIFrameElement);
  }

  private inspectScript(s: HTMLScriptElement): void {
    const mark = s.getAttribute(ATTR);
    if (mark === 'ignore' || mark === 'held') return;
    const manual = s.getAttribute(ATTR) as OptionalCategory | null;
    const typeAttr = s.getAttribute('type');
    let category: OptionalCategory | null = null;

    const isManual = !!manual && typeAttr === MANUAL_TYPE;
    let after: HTMLScriptElement | undefined;
    if (isManual) {
      category = manual;
    } else if (typeAttr !== MANUAL_TYPE) {
      // Plain script, or one already neutralised by the createElement patch.
      category = this.categoryFor(s.src || s.getAttribute('src'));
      // Inline code that calls a library we are holding waits for that library.
      if (!category && !s.src && !s.getAttribute('src')) {
        const text = s.text || '';
        for (const d of DEPENDENTS) {
          if (!d.global.test(text)) continue;
          const lib = this.blocked.find((b): b is BlockedScript => b.kind === 'script' && (b.node.getAttribute('src') || '').includes(d.source));
          if (lib) {
            category = lib.category;
            after = lib.node;
            break;
          }
        }
      }
    }
    if (!category) return;
    if (this.granted.has(category)) {
      if (typeAttr === MANUAL_TYPE || typeAttr === BLOCKED_TYPE) this.activate({ kind: 'script', category, node: s, manual: isManual });
      return;
    }
    if (this.blocked.some((b) => b.node === s)) return;

    let stand: HTMLScriptElement | undefined;
    if (!isManual && !this.dynamic.has(s)) {
      const realType = s.getAttribute('type');
      if (realType && realType !== BLOCKED_TYPE) s.setAttribute(`${ATTR}-type`, realType);
      s.type = BLOCKED_TYPE;
      // Firefox only: cancels execution of a parser-inserted script.
      s.addEventListener('beforescriptexecute', (e) => {
        if (s.getAttribute('type') === BLOCKED_TYPE) e.preventDefault();
      });
      // Taking the node out stops Chromium/Safari from executing a parser-inserted external script.
      // An inert copy takes its place, so server-rendered markup keeps its shape for hydration and
      // the script comes back in the same spot.
      stand = s.cloneNode(false) as HTMLScriptElement;
      stand.setAttribute(ATTR, 'held');
      if (s.parentNode) s.parentNode.replaceChild(stand, s);
      else stand = undefined;
    }
    // Page-created scripts carry a non-JS type from the src setter, so they are inert where they
    // are; they stay in place and are released as the same node. Manual text/plain tags too.
    this.blocked.push({ kind: 'script', category, node: s, manual: isManual, stand, after });
  }

  private inspectFrame(f: HTMLIFrameElement): void {
    if (f.getAttribute(ATTR) === 'ignore') return;
    const stored = f.getAttribute(`${ATTR}-src`);
    const src = stored ?? f.getAttribute('src');
    const manualCat = f.getAttribute(ATTR) as OptionalCategory | null;
    const category = manualCat ?? this.categoryFor(src);
    if (!category || !src) return;
    const manual = !!manualCat;
    if (this.granted.has(category)) {
      if (stored) this.activate({ kind: 'iframe', category, node: f, src, manual });
      return;
    }
    if (this.blocked.some((b) => b.node === f)) return;
    f.setAttribute(`${ATTR}-src`, src);
    f.removeAttribute('src');
    f.src = 'about:blank';
    this.blocked.push({ kind: 'iframe', category, node: f, src, manual });
    this.placeholder?.(f, category);
  }

  private activate(b: Blocked): void {
    if (b.kind === 'iframe') {
      b.node.removeAttribute(`${ATTR}-src`);
      b.node.removeAttribute('srcdoc');
      b.node.src = b.src;
      return;
    }
    const old = b.node;
    // Inline code waiting on a library: run once the library's released copy has loaded.
    if (b.after) {
      const lib = this.released.get(b.after);
      if (lib && !this.loaded.has(lib)) {
        const later = { ...b, after: undefined };
        lib.addEventListener('load', () => this.activate(later), { once: true });
        return;
      }
    }
    // A page-created script never started (its type was not JavaScript), so restoring its type and
    // setting src again runs it as the same element: onload handlers and loader promises fire.
    if (this.dynamic.has(old) && !b.manual && this.srcSet && this.typeSet) {
      const realType = old.getAttribute(`${ATTR}-type`);
      old.removeAttribute(`${ATTR}-type`);
      if (realType) this.typeSet(old, realType);
      else old.removeAttribute('type');
      old.setAttribute(ATTR, 'ignore');
      const src = old.getAttribute('src');
      if (src) this.srcSet(old, src);
      return;
    }
    const fresh = document.createElement('script');
    fresh.setAttribute(ATTR, 'ignore');
    for (const a of Array.from(old.attributes)) {
      if (a.name === 'type' || a.name === ATTR || a.name === `${ATTR}-type`) continue;
      fresh.setAttribute(a.name, a.value);
    }
    // The original type (e.g. module) comes back; manual text/plain tags ran as classic scripts.
    const realType = old.getAttribute(`${ATTR}-type`);
    if (realType) fresh.setAttribute('type', realType);
    // A nonce reads as "" once set, so copying attributes loses it; copy the property.
    if (old.nonce) fresh.nonce = old.nonce;
    if (old.src) fresh.src = old.src;
    else fresh.text = old.text;
    // Back where it was: in place of its stand-in, or of the node itself, else at the end of head.
    if (fresh.src) {
      this.released.set(old, fresh);
      fresh.addEventListener('load', () => this.loaded.add(fresh), { once: true });
    }
    const spot = b.stand?.parentNode ? b.stand : old.parentNode ? old : null;
    if (spot) spot.parentNode!.replaceChild(fresh, spot);
    else (document.head || document.documentElement).appendChild(fresh);
  }

  /** Neutralise dynamically created scripts and iframes before a src is even assigned. */
  private patchCreateElement(): void {
    const create = document.createElement;
    const srcDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src');
    const typeDesc = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'type');
    const frameSrcDesc = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'src');
    if (!srcDesc?.set || !typeDesc?.set || !typeDesc.get) return;
    const self = this;
    this.srcSet = (el, v) => srcDesc.set!.call(el, v);
    this.typeSet = (el, v) => typeDesc.set!.call(el, v);
    document.createElement = function (this: Document, tag: string, options?: ElementCreationOptions) {
      const el = create.call(this, tag, options);
      if (typeof tag !== 'string') return el;
      const name = tag.toLowerCase();
      if (name === 'iframe' && frameSrcDesc?.set) {
        const frame = el as HTMLIFrameElement;
        try {
          // Our check, called directly: another script may replace the src property on this element
          // too, and routing through it from setAttribute could bounce between the two forever.
          const setFrameSrc = function (this: HTMLIFrameElement, value: string) {
              let cat: OptionalCategory | null = null;
              try {
                cat = self.isActive() ? self.categoryFor(value) : null;
              } catch {
                cat = null; // never let a check stop a frame from loading
              }
              if (cat && !self.granted.has(cat) && frame.getAttribute(ATTR) !== 'ignore') {
                // Park the real URL and register the frame now: frames inside shadow DOM (web
                // components) are never seen by the document observer.
                const url = String(value);
                frame.setAttribute(`${ATTR}-src`, url);
                frameSrcDesc.set!.call(this, 'about:blank');
                if (!self.blocked.some((b) => b.node === frame)) {
                  self.blocked.push({ kind: 'iframe', category: cat, node: frame, src: url, manual: false });
                  self.placeholder?.(frame, cat);
                }
                return;
              }
              frameSrcDesc.set!.call(this, value);
          };
          Object.defineProperty(frame, 'src', {
            configurable: true,
            enumerable: true,
            get() {
              return frameSrcDesc.get!.call(this);
            },
            set: setFrameSrc,
          });
          const setAttr = frame.setAttribute.bind(frame);
          frame.setAttribute = (n: string, v: string) => (n === 'src' ? setFrameSrc.call(frame, v) : setAttr(n, v));
        } catch {
          /* fall back to observer-only */
        }
        return el;
      }
      if (name !== 'script') return el;
      const script = el as HTMLScriptElement;
      self.dynamic.add(script);
      try {
        // Our check, called directly from setAttribute too (see the iframe case above).
        const setScriptSrc = function (this: HTMLScriptElement, value: string) {
            let cat: OptionalCategory | null = null;
            try {
              cat = self.isActive() ? self.categoryFor(value) : null;
            } catch {
              cat = null; // never let a check stop a script from loading
            }
            if (cat && !self.granted.has(cat) && script.getAttribute(ATTR) !== 'ignore') {
              // Remember the real type (module, importmap…) so it comes back on release.
              if (script.type && script.type !== BLOCKED_TYPE) script.setAttribute(`${ATTR}-type`, script.type);
              typeDesc.set!.call(this, BLOCKED_TYPE);
            }
            srcDesc.set!.call(this, value);
        };
        Object.defineProperty(script, 'src', {
          configurable: true,
          enumerable: true,
          get() {
            return srcDesc.get!.call(this);
          },
          set: setScriptSrc,
        });
        const setAttribute = script.setAttribute.bind(script);
        script.setAttribute = (name: string, value: string) => {
          if (name === 'src') setScriptSrc.call(script, value);
          else if (name === 'type' && script.getAttribute('type') === BLOCKED_TYPE) setAttribute(`${ATTR}-type`, value);
          else setAttribute(name, value);
        };
      } catch {
        /* another script patched createElement; fall back to observer-only */
      }
      return el;
    } as typeof document.createElement;
  }
}
